import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const Analytics = ({ adminKey, onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/analytics", {
        headers: { "X-TAG-Admin-Key": adminKey },
        validateStatus: () => true,
      });
      if (res.status === 401) { onUnauthorized(); return; }
      if (res.status >= 200 && res.status < 300) setData(res.data);
      else toast.error("Failed to load analytics.");
    } finally { setLoading(false); }
  }, [adminKey, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="tag-card" data-testid="analytics-loading" style={{ textAlign: "center", color: "#777" }}>Loading analytics…</div>;
  }

  const pct = (n) => `${(n * 100).toFixed(1)}%`;

  return (
    <div data-testid="analytics-root">
      {/* LEADS */}
      <SectionHeader title="Leads" onRefresh={load} />
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 28 }}>
        <Kpi label="Score Submissions" value={data.leads.score_submissions} color="#fff" />
        <Kpi label="Tool Completions" value={data.leads.tool_completions} color="#fff" />
        <Kpi label="Resource Requests" value={data.leads.resource_requests} color="#fff" />
        <Kpi label="Subscribers" value={data.leads.subscribers} color="#FF6575" />
        <Kpi label="Contact Messages" value={data.leads.contact_messages} color="#fff" />
      </div>

      {/* ATLAS */}
      <SectionHeader title="Atlas" />
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 28 }}>
        <Kpi label="Sessions" value={data.atlas.sessions} color="#fff" />
        <Kpi label="Messages" value={data.atlas.messages} color="#fff" />
      </div>

      {/* PURCHASES */}
      <SectionHeader title="Purchases" />
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 28 }}>
        <Kpi label="Audit ($49)" value={data.purchases.audit_paid} color="#3aab5a" />
        <Kpi label="Blueprint ($497)" value={data.purchases.blueprint_paid} color="#3aab5a" />
        <Kpi label="Launch Kit ($2.5K)" value={data.purchases.launch_kit_paid} color="#3aab5a" />
        <Kpi label="Total Paid" value={data.purchases.total_paid} color="#FF6575" />
        <Kpi label="Pending" value={data.purchases.pending} color="#ba7a30" />
      </div>

      {/* CONVERSIONS */}
      <SectionHeader title="Conversion rates" />
      <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <Kpi label="Score → Audit" value={pct(data.conversions.score_to_audit)} color="#fff" />
        <Kpi label="Score → Subscriber" value={pct(data.conversions.score_to_subscriber)} color="#fff" />
        <Kpi label="Subscriber → Audit" value={pct(data.conversions.subscriber_to_audit)} color="#fff" />
      </div>

      <style>{`@media (max-width: 760px){ .kpi-grid{ grid-template-columns: repeat(2, 1fr) !important; } }`}</style>
    </div>
  );
};

const SectionHeader = ({ title, onRefresh }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
    <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h2>
    {onRefresh && <button onClick={onRefresh} data-testid="analytics-refresh" style={{ background: "transparent", border: "none", color: "#777", fontSize: 12, cursor: "pointer" }}>↻ Refresh</button>}
  </div>
);

const Kpi = ({ label, value, color }) => (
  <div className="tag-card" style={{ padding: "14px 16px" }}>
    <div style={{ color, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    <div style={{ color: "#888", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{label}</div>
  </div>
);
